import { ModelRow, ObjectUtil } from "@mcleod/core";
import { BehaviorTypes, Button, Component, DataSourceMode, ValidationResult } from "../..";
import { MultiButton } from "../../components/multibutton/MultiButton";
import { MultiButtonProps } from "../../components/multibutton/MultiButtonProps";
import { PanelProps } from "../../components/panel/PanelProps";
import { EditRowDecoratorProps } from "./EditRowDecoratorProps";
import { SlideoutDecorator } from "./SlideoutDecorator";
import { EditRowDecoratorBehavior } from "./EditRowDecoratorBehavior";

export class EditRowDecorator extends SlideoutDecorator implements EditRowDecoratorProps {
    private static _editRowDecoratorBehavior: EditRowDecoratorBehavior;
    private _dataSourceMode: DataSourceMode;
    data: ModelRow;
    multiButton: MultiButton;
    btnDelete: Button;
    saveButtonProps: Partial<MultiButtonProps>;
    onDelete: () => void;
    onSave: (updatedData: ModelRow | any) => void | Promise<void>;
    onValidate: (updatedData: ModelRow | any) => ValidationResult[];
    onAddAnother: () => void;

    constructor(props?: Partial<EditRowDecoratorProps>) {
        super(props);
        this.themeKey = "editRowDecorator.outerPanel"; //applies EditRowDecorator theme on top of the SlideoutDecorator theme applied in super()
    }

    public get dataSourceMode(): DataSourceMode {
        return this._dataSourceMode ?? DataSourceMode.UPDATE;
    }

    public set dataSourceMode(value: DataSourceMode) {
        this._dataSourceMode = value;
    }

    override createContentPanel(props: Partial<PanelProps>) {
        if (ObjectUtil.containsKey(props, "padding") !== true) {
            if (props == null)
                props = {};
        }
        super.createContentPanel(props);
    }

    override addHeaderActions(addlComponents: Component[]): void {
        this.addComponentsToHeader(addlComponents);
        this.addDeleteButton();
        this.editRowDecoratorBehavior.addSaveButton(
            this, this.saveAndAddAnotherClicked.bind(this), this.saveAndCloseClicked.bind(this)
        );
        this.addCloseButton();
    }

    addDeleteButton() {
        if (DataSourceMode.SEARCH !== this.dataSourceMode && this.onDelete != null) {
            this.btnDelete = new Button({ themeKey: "editRowDecorator.deleteButton", id: "erdDeleteButton", rowBreak: false });
            this.btnDelete.addClickListener(() => this.onDelete());
            this.panelHeader.add(this.btnDelete);
        }
    }

    override addLayout(): void {
        if (this.data != null && this.layout != null) {
            this.layout.mainDataSource.setRowsAndMode(this.dataSourceMode, [this.data]);
        }
        super.addLayout();
    }

    private async saveAndCloseClicked(): Promise<void> {
        if (await this.save() && this.onClose != null)
            this.onClose(false);
    }

    private async saveAndAddAnotherClicked(): Promise<void> {
        if (await this.save() && this.onAddAnother != null)
            this.onAddAnother();
    }

    private async save(): Promise<boolean> {
        if (this.validateSimple(true) !== true)
            return false;
        if (this.onSave != null)
            await this.onSave(this.data);
        return true;
    }

    override validate(checkRequired: boolean = true, showErrors: boolean = true): ValidationResult[] {
        let result = super.validate(checkRequired, showErrors);
        if (this.onValidate != null) {
            const onValidateResult = this.onValidate(this.data);
            if (result == null)
                result = onValidateResult;
            else
                result = result.concat(onValidateResult);
        }
        return result;
    }

    get fillVerticalSpace(): boolean {
        return this._fillVerticalSpace == null ? true : super.fillVerticalSpace;
    }

    set fillVerticalSpace(value: boolean) {
        super.fillVerticalSpace = value;
    }

    private get editRowDecoratorBehavior(): EditRowDecoratorBehavior {
        EditRowDecorator._editRowDecoratorBehavior ??= BehaviorTypes.get("EditRowDecorator");
        return EditRowDecorator._editRowDecoratorBehavior;
    }
}
